import { format, isValid } from "date-fns";
import { DeviceHistoryItem } from "types/deviceHooks.types";
import {
  EventTypeChip,
  SourceChip,
} from "styles/devices/deviceHistory.styles";
import { formatHistoryDetails } from "./formatHistoryDetails";

const EVENT_TYPE_LABELS: Record<string, string> = {
  ATTACH_DEVICE_TO_USER: "Device Linked",
  DETACH_DEVICE_FROM_USER: "Device Unlinked",
  SCHEDULE_UPDATE: "Schedule Updated",
  CONFIG_UPDATED: "Config Updated",
  FIRMWARE_UPDATE: "Firmware Updated",
  NIPPER_UPDATE: "Nipper Updated",
  ALIAS_UPDATE: "Alias Updated",
  LOCATION_UPDATE: "Location Updated",
  DEVICE_LOCATION_UPDATED: "Location Updated",
  MODE_UPDATE: "Mode Updated",
  CHANGE_MODES: "Mode Updated",
  TARGET_UPDATE: "Targets Updated",
  CHANGE_TARGETS: "Targets Updated",
  ADD_HEAT_PUMP: "Heat Pump Added",
  DEVICE_UPDATE: "Device Updated",
  SHADOW_UPDATED: "Device Shadow Updated",
  OTA_UPDATE_REQUESTED: "Firmware Update Requested",
  DEVICE_CONNECTION_UPDATE: "Connection Updated",
  LIMIT_EXCEEDED: "Limit Exceeded",
  ALARM_CREATED: "Alarm Created",
  NOTIFICATION_CREATED: "Notification Created",
};

const SOURCE_LABELS: Record<string, string> = {
  APP: "App",
  PORTAL: "Portal",
  DEVICE: "Device",
};

export function formatEventType(eventType: string): string {
  return (
    EVENT_TYPE_LABELS[eventType] ||
    eventType
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function eventTypeTemplate(rowData: DeviceHistoryItem) {
  return (
    <EventTypeChip eventType={rowData.eventType}>
      {formatEventType(rowData.eventType)}
    </EventTypeChip>
  );
}

export function sourceTemplate(rowData: DeviceHistoryItem) {
  return (
    <SourceChip source={rowData.changedFrom}>
      {SOURCE_LABELS[rowData.changedFrom] ?? rowData.changedFrom}
    </SourceChip>
  );
}

export function dateTemplate(rowData: DeviceHistoryItem) {
  try {
    const date = new Date(rowData.createdAt);
    if (!isValid(date)) return <span>—</span>;
    return <span>{format(date, "MMM dd, yyyy HH:mm")}</span>;
  } catch {
    return <span>—</span>;
  }
}

export function actionTemplate(rowData: DeviceHistoryItem) {
  if (!rowData.event) return <span>—</span>;
  return (
    <span style={{ whiteSpace: "pre-line" }}>
      {formatHistoryDetails(rowData.event, {
        dealerName: rowData.dealer?.name,
        userDisplayName: rowData.userDisplayName,
        actorDisplayName: rowData.actorDisplayName,
      })}
    </span>
  );
}

export function matchesHistoryFilters(
  item: DeviceHistoryItem,
  filterEventType: string | null,
  filterSource: string | null,
  filterText: string,
): boolean {
  if (filterEventType && item.eventType !== filterEventType) return false;
  if (filterSource && item.changedFrom !== filterSource) return false;
  if (!filterText) return true;

  const search = filterText.toLowerCase();
  const details = formatHistoryDetails(item.event ?? {}, {
    dealerName: item.dealer?.name,
    userDisplayName: item.userDisplayName,
    actorDisplayName: item.actorDisplayName,
  }).toLowerCase();
  const eventLabel = formatEventType(item.eventType).toLowerCase();
  const userName = (item.userDisplayName || "").toLowerCase();
  const actorName = (item.actorDisplayName || "").toLowerCase();
  let dateStr = "";
  try {
    const d = new Date(item.createdAt);
    dateStr = isValid(d) ? format(d, "MMM dd, yyyy HH:mm").toLowerCase() : "";
  } catch {
    dateStr = "";
  }

  return (
    details.includes(search) ||
    eventLabel.includes(search) ||
    userName.includes(search) ||
    actorName.includes(search) ||
    dateStr.includes(search)
  );
}
